import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Building2, Users, Calendar, Search, Snowflake, PlayCircle, Trash2, AlertTriangle,
  ChevronDown, ChevronRight, Mail, Shield, Plus, CheckCircle, XCircle, TrendingUp,
  DollarSign, FileText, UserCheck, ExternalLink, RefreshCw, AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface TenantWithUserCount {
  id: string;
  name: string;
  slug: string;
  planType: string;
  currentPlanId: string | null;
  maxUsers: number;
  maxGoals: number;
  isActive: boolean;
  status: string;
  subscriptionStatus: string | null;
  lastFailedPaymentDate: string | null;
  createdAt: string;
  updatedAt: string;
  userCount: number;
  users: TenantUser[];
}

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  price: number;
  interval: string;
}

interface Invoice {
  id: string;
  tenantId: string;
  stripeInvoiceId: string;
  invoiceNumber: string | null;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  createdAt: string;
}

interface AdminMetrics {
  mrr: number;
  arr: number;
  activeTenants: number;
  planBreakdown: Record<string, number>;
  newSignups: { last30d: number; last7d: number; today: number };
  churned30d: number;
  pastDue: { count: number; tenants: Array<{ id: string; name: string; lastFailedPaymentDate: string | null }> };
  revenue: { last30dCents: number; last12moCents: number };
  totalTenants: number;
}

type ConfirmAction = {
  type: 'freeze' | 'unfreeze' | 'soft-delete' | 'restore' | 'hard-delete' | 'impersonate';
  tenant: TenantWithUserCount;
  user?: TenantUser;
} | null;

function fmtMoney(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

function StatCard({ title, value, sub, icon: Icon, color = "blue" }: {
  title: string; value: string | number; sub?: string; icon: any; color?: string;
}) {
  const colors: Record<string, string> = {
    blue: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20",
    green: "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20",
    red: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20",
    orange: "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20",
    purple: "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20",
  };
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colors[color] ?? colors.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function RevenueDashboard({ tenants }: { tenants: TenantWithUserCount[] }) {
  const { data: metrics, isLoading, refetch } = useQuery<AdminMetrics>({
    queryKey: ["/api/admin/metrics"],
  });

  if (isLoading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />)}</div>;
  if (!metrics) return <p className="text-muted-foreground">Failed to load metrics.</p>;

  const planEntries = Object.entries(metrics.planBreakdown).sort((a,b) => b[1]-a[1]);
  const totalPlans = planEntries.reduce((s,[,n])=>s+n,0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Revenue & Usage Overview</h3>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard title="MRR" value={fmtMoney(metrics.mrr * 100)} icon={DollarSign} color="green" sub="Monthly Recurring Revenue" />
        <StatCard title="ARR" value={fmtMoney(metrics.arr * 100)} icon={TrendingUp} color="green" sub="Annual Recurring Revenue" />
        <StatCard title="Active Tenants" value={metrics.activeTenants} icon={Building2} color="blue" sub={`of ${metrics.totalTenants} total`} />
        <StatCard title="New (30d)" value={metrics.newSignups.last30d} icon={Users} color="purple" sub={`${metrics.newSignups.last7d} last 7d, ${metrics.newSignups.today} today`} />
        <StatCard title="Churned (30d)" value={metrics.churned30d} icon={XCircle} color="red" />
        <StatCard title="Past Due" value={metrics.pastDue.count} icon={AlertCircle} color="orange" sub="accounts behind on payments" />
        <StatCard title="Rev (30d)" value={fmtMoney(metrics.revenue.last30dCents)} icon={FileText} color="green" />
        <StatCard title="Rev (12mo)" value={fmtMoney(metrics.revenue.last12moCents)} icon={FileText} color="green" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Plan Distribution</h4>
          <div className="space-y-2">
            {planEntries.map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-3">
                <span className="text-sm capitalize w-24 shrink-0">{plan}</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all"
                    style={{ width: totalPlans ? `${(count/totalPlans)*100}%` : "0%" }}
                  />
                </div>
                <span className="text-sm font-medium w-10 text-right">{count}</span>
                <span className="text-xs text-muted-foreground w-12 text-right">
                  {totalPlans ? Math.round((count/totalPlans)*100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-orange-500" />
            Past-Due Tenants
          </h4>
          {metrics.pastDue.tenants.length === 0 ? (
            <p className="text-sm text-muted-foreground">No past-due tenants.</p>
          ) : (
            <div className="space-y-2">
              {metrics.pastDue.tenants.map(t => (
                <div key={t.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                  <span className="font-medium">{t.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {t.lastFailedPaymentDate ? `Failed ${format(new Date(t.lastFailedPaymentDate), "MMM d")}` : "Past due"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function InvoicesTab({ tenantId }: { tenantId?: string }) {
  const { data: invoices = [], isLoading } = useQuery<Invoice[]>({
    queryKey: tenantId ? ["/api/admin/invoices", tenantId] : ["/api/admin/invoices"],
    queryFn: async () => {
      const url = tenantId ? `/api/admin/invoices?tenantId=${tenantId}` : "/api/admin/invoices";
      const res = await fetch(url, { credentials: "include" });
      return res.json();
    },
  });

  function statusColor(s: string) {
    if (s === "paid") return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    if (s === "open") return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    if (s === "void" || s === "uncollectible") return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    return "bg-muted text-muted-foreground";
  }

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i=><div key={i} className="h-10 bg-muted animate-pulse rounded"/>)}</div>;

  if (!invoices.length) return (
    <div className="text-center py-12">
      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
      <p className="text-muted-foreground">No invoices yet. Invoices are created automatically via Stripe webhooks.</p>
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Amount Due</TableHead>
            <TableHead>Amount Paid</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map(inv => (
            <TableRow key={inv.id}>
              <TableCell className="font-mono text-sm">{inv.invoiceNumber ?? inv.stripeInvoiceId.slice(0,12)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{format(new Date(inv.createdAt), "MMM d, yyyy")}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {inv.periodStart && inv.periodEnd
                  ? `${format(new Date(inv.periodStart), "MMM d")} – ${format(new Date(inv.periodEnd), "MMM d, yyyy")}`
                  : "—"}
              </TableCell>
              <TableCell>{fmtMoney(inv.amountDue, inv.currency)}</TableCell>
              <TableCell>{fmtMoney(inv.amountPaid, inv.currency)}</TableCell>
              <TableCell>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(inv.status)}`}>
                  {inv.status}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  {inv.hostedInvoiceUrl && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </a>
                    </Button>
                  )}
                  {inv.invoicePdfUrl && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={inv.invoicePdfUrl} target="_blank" rel="noopener noreferrer">
                        <FileText className="w-3 h-3 mr-1" />
                        PDF
                      </a>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AdminTenantsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());
  const [mainTab, setMainTab] = useState("tenants");

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [familyName, setFamilyName] = useState("");
  const [nameCheckResult, setNameCheckResult] = useState<{ available: boolean; message: string } | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const { data: tenants, isLoading } = useQuery<TenantWithUserCount[]>({
    queryKey: ["/api/admin/tenants"],
    enabled: !!(user as any)?.isMasterAdmin,
  });

  const { data: subscriptionPlans = [], isLoading: isLoadingPlans } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
    enabled: !!(user as any)?.isMasterAdmin,
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ tenantId, planId }: { tenantId: string; planId: string | null }) =>
      apiRequest("PATCH", `/api/admin/tenants/${tenantId}/plan`, { planId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] });
      toast({ title: "Success", description: "Pricing plan updated successfully" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const freezeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/tenants/${id}/freeze`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setConfirmAction(null); toast({ title: "Tenant frozen" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const unfreezeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/tenants/${id}/unfreeze`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setConfirmAction(null); toast({ title: "Tenant unfrozen" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const softDeleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/tenants/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setConfirmAction(null); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/tenants/${id}/restore`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setConfirmAction(null); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/tenants/${id}/hard`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setConfirmAction(null); setDeleteConfirmText(""); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const createTenantMutation = useMutation({
    mutationFn: (name: string) => apiRequest("POST", "/api/admin/tenants", { name }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/admin/tenants"] }); setCreateDialogOpen(false); setFamilyName(""); setNameCheckResult(null); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const impersonateMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/impersonate/${userId}`),
    onSuccess: () => {
      // The session cookie now belongs to the impersonated user. Drop the
      // entire old session's cache before the delayed hard reload.
      queryClient.clear();
      setConfirmAction(null);
      toast({ title: "Impersonation started", description: "Reloading page..." });
      setTimeout(() => window.location.reload(), 800);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    if (!familyName.trim()) { setNameCheckResult(null); return; }
    setIsCheckingName(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/admin/tenants/check-name?name=${encodeURIComponent(familyName.trim())}`);
        const data = await res.json();
        setNameCheckResult({ available: data.available, message: data.message });
      } catch { /* silent */ } finally { setIsCheckingName(false); }
    }, 500);
    return () => { clearTimeout(timer); setIsCheckingName(false); };
  }, [familyName]);

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    switch (confirmAction.type) {
      case "freeze": freezeMutation.mutate(confirmAction.tenant.id); break;
      case "unfreeze": unfreezeMutation.mutate(confirmAction.tenant.id); break;
      case "soft-delete": softDeleteMutation.mutate(confirmAction.tenant.id); break;
      case "restore": restoreMutation.mutate(confirmAction.tenant.id); break;
      case "hard-delete": if (deleteConfirmText === "DELETE") hardDeleteMutation.mutate(confirmAction.tenant.id); break;
      case "impersonate": if (confirmAction.user) impersonateMutation.mutate(confirmAction.user.id); break;
    }
  };

  const toggleTenantExpansion = (tenantId: string) => {
    setExpandedTenants(prev => {
      const s = new Set(prev);
      s.has(tenantId) ? s.delete(tenantId) : s.add(tenantId);
      return s;
    });
  };

  if (!(user as any)?.isMasterAdmin) {
    return (
      <div className="p-6"><Card className="p-6"><p className="text-muted-foreground">Access denied. Master admin privileges required.</p></Card></div>
    );
  }

  const filteredTenants = tenants?.filter(t =>
    (t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.slug.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (statusFilter === "all" || t.status === statusFilter)
  );

  const getStatusBadge = (status: string) => {
    if (status === "active") return <Badge className="bg-green-500 hover:bg-green-600" data-testid="badge-status-active">Active</Badge>;
    if (status === "frozen") return <Badge className="bg-orange-500 hover:bg-orange-600" data-testid="badge-status-frozen">Frozen</Badge>;
    if (status === "deleted") return <Badge variant="destructive" data-testid="badge-status-deleted">Deleted</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Building2 className="w-8 h-8" /> Tenant Management</h1>
          <p className="text-muted-foreground mt-2">Manage all tenants in the system (Master Admin)</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-tenant"><Plus className="w-4 h-4 mr-2" />Create New Tenant</Button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-create-tenant" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>Create a new tenant manually by entering a family name.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="familyName">Family Name *</Label>
                <Input id="familyName" data-testid="input-family-name" placeholder="Enter family name..." value={familyName} onChange={e => setFamilyName(e.target.value)} className="mt-1" />
                {isCheckingName && <p className="text-sm text-muted-foreground mt-2">Checking availability...</p>}
                {nameCheckResult && !isCheckingName && (
                  <div className={`mt-2 flex items-center gap-2 text-sm ${nameCheckResult.available ? "text-green-600" : "text-red-600"}`}>
                    {nameCheckResult.available ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span data-testid={nameCheckResult.available ? "text-name-available" : "text-name-taken"}>{nameCheckResult.message}</span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setFamilyName(""); setNameCheckResult(null); }} data-testid="button-cancel-create">Cancel</Button>
              <Button onClick={() => createTenantMutation.mutate(familyName.trim())} disabled={!familyName.trim() || !nameCheckResult?.available || createTenantMutation.isPending} data-testid="button-submit-create">
                {createTenantMutation.isPending ? "Creating..." : "Create Tenant"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="tenants"><Building2 className="w-4 h-4 mr-2" />Tenants</TabsTrigger>
          <TabsTrigger value="revenue"><TrendingUp className="w-4 h-4 mr-2" />Revenue</TabsTrigger>
          <TabsTrigger value="invoices"><FileText className="w-4 h-4 mr-2" />Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-4">
          <Card>
            <div className="p-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search by name or slug..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="input-search" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="select-status-filter"><SelectValue placeholder="Filter by status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="frozen">Frozen</SelectItem>
                    <SelectItem value="deleted">Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isLoading ? (
                <div className="space-y-4">{[1,2,3].map(i=><div key={i} className="h-16 bg-muted animate-pulse rounded"/>)}</div>
              ) : filteredTenants && filteredTenants.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table data-testid="table-tenants">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTenants.map(tenant => {
                        const isExpanded = expandedTenants.has(tenant.id);
                        return (
                          <>
                            <TableRow key={tenant.id} data-testid={`row-tenant-${tenant.id}`}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => toggleTenantExpansion(tenant.id)} data-testid={`button-expand-${tenant.id}`}>
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </Button>
                                  <Building2 className="w-4 h-4 text-muted-foreground" />
                                  {tenant.name}
                                </div>
                              </TableCell>
                              <TableCell><code className="text-sm bg-muted px-2 py-1 rounded">{tenant.slug}</code></TableCell>
                              <TableCell>
                                <Select value={tenant.currentPlanId || "none"} onValueChange={val => updatePlanMutation.mutate({ tenantId: tenant.id, planId: val === "none" ? null : val })} disabled={isLoadingPlans || updatePlanMutation.isPending}>
                                  <SelectTrigger className="w-[180px]" data-testid={`select-plan-${tenant.id}`}><SelectValue placeholder="Select plan" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No Plan</SelectItem>
                                    {subscriptionPlans.map(p => <SelectItem key={p.id} value={p.id}>{p.displayName} - ${(p.price/100).toFixed(2)}/{p.interval}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>{getStatusBadge(tenant.status)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" />{tenant.userCount}</div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="w-4 h-4" />{tenant.createdAt ? format(new Date(tenant.createdAt), "MMM d, yyyy") : "—"}</div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  {tenant.status === "active" && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: "freeze", tenant })} data-testid={`button-freeze-${tenant.id}`}><Snowflake className="w-4 h-4 mr-1" />Freeze</Button>
                                      <Button size="sm" variant="destructive" onClick={() => setConfirmAction({ type: "soft-delete", tenant })} data-testid={`button-soft-delete-${tenant.id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                                    </>
                                  )}
                                  {tenant.status === "frozen" && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: "unfreeze", tenant })} data-testid={`button-unfreeze-${tenant.id}`}><PlayCircle className="w-4 h-4 mr-1" />Unfreeze</Button>
                                      <Button size="sm" variant="destructive" onClick={() => setConfirmAction({ type: "soft-delete", tenant })} data-testid={`button-soft-delete-${tenant.id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                                    </>
                                  )}
                                  {tenant.status === "deleted" && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: "restore", tenant })} data-testid={`button-restore-${tenant.id}`}><PlayCircle className="w-4 h-4 mr-1" />Restore</Button>
                                      <Button size="sm" variant="destructive" onClick={() => setConfirmAction({ type: "hard-delete", tenant })} data-testid={`button-hard-delete-${tenant.id}`}><AlertTriangle className="w-4 h-4 mr-1" />Permanently Delete</Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow key={`${tenant.id}-users`} data-testid={`row-users-${tenant.id}`}>
                                <TableCell colSpan={7} className="bg-muted/50 dark:bg-muted/20">
                                  <div className="py-3 px-8">
                                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4" />Users ({tenant.userCount})</h4>
                                    {tenant.users.length > 0 ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {tenant.users.map(u => (
                                          <div key={u.id} className="flex items-start gap-3 p-3 bg-background rounded-lg border" data-testid={`user-card-${u.id}`}>
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-sm flex items-center gap-2">
                                                {u.name}
                                                {u.role === "admin" && <Badge variant="secondary" className="text-xs"><Shield className="w-3 h-3 mr-1" />Admin</Badge>}
                                              </div>
                                              {u.email && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{u.email}</span></div>}
                                            </div>
                                            <Button size="sm" variant="outline" className="shrink-0 text-xs" onClick={() => setConfirmAction({ type: "impersonate", tenant, user: u })}>
                                              <UserCheck className="w-3 h-3 mr-1" />Impersonate
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-muted-foreground">No users found</p>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">No tenants found</div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="mt-4">
          <Card className="p-6">
            {mainTab === "revenue" && <RevenueDashboard tenants={tenants ?? []} />}
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">All Invoices</h3>
            {mainTab === "invoices" && <InvoicesTab />}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialogs */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => { setConfirmAction(null); setDeleteConfirmText(""); }}>
        <AlertDialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "freeze" && "Freeze Tenant Account?"}
              {confirmAction?.type === "unfreeze" && "Reactivate Tenant Account?"}
              {confirmAction?.type === "soft-delete" && "Delete Tenant?"}
              {confirmAction?.type === "restore" && "Restore Tenant?"}
              {confirmAction?.type === "hard-delete" && "PERMANENTLY Delete Tenant?"}
              {confirmAction?.type === "impersonate" && `Impersonate ${confirmAction.user?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {confirmAction?.type === "freeze" && <p>Users in <strong>{confirmAction.tenant.name}</strong> will be immediately logged out and unable to access the application. This can be reversed.</p>}
              {confirmAction?.type === "unfreeze" && <p><strong>{confirmAction.tenant.name}</strong> will be reactivated and users will be able to access the application again.</p>}
              {confirmAction?.type === "soft-delete" && <p>This will mark <strong>{confirmAction.tenant.name}</strong> as deleted and block all access. Data will be retained and can be restored.</p>}
              {confirmAction?.type === "restore" && <p>This will restore <strong>{confirmAction.tenant.name}</strong> and reactivate all user access.</p>}
              {confirmAction?.type === "hard-delete" && (
                <div className="space-y-3">
                  <p className="text-destructive font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5" />WARNING: This action CANNOT be undone!</p>
                  <p>This will permanently delete <strong>{confirmAction.tenant.name}</strong> and ALL associated data.</p>
                  <div className="space-y-2">
                    <p className="font-medium">Type <code className="bg-muted px-1 rounded">DELETE</code> to confirm:</p>
                    <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE" data-testid="input-delete-confirm" />
                  </div>
                </div>
              )}
              {confirmAction?.type === "impersonate" && (
                <p>You will view the app as <strong>{confirmAction.user?.email ?? confirmAction.user?.name}</strong> in tenant <strong>{confirmAction.tenant.name}</strong>. A yellow banner will show while impersonating. All actions will be logged.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={(confirmAction?.type === "hard-delete" && deleteConfirmText !== "DELETE") || impersonateMutation.isPending}
              className={confirmAction?.type === "freeze" || confirmAction?.type === "soft-delete" || confirmAction?.type === "hard-delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : confirmAction?.type === "impersonate" ? "bg-yellow-500 hover:bg-yellow-600 text-black" : ""}
              data-testid="button-confirm-action"
            >
              {confirmAction?.type === "freeze" && "Freeze Account"}
              {confirmAction?.type === "unfreeze" && "Reactivate Account"}
              {confirmAction?.type === "soft-delete" && "Delete Tenant"}
              {confirmAction?.type === "restore" && "Restore Tenant"}
              {confirmAction?.type === "hard-delete" && "Permanently Delete"}
              {confirmAction?.type === "impersonate" && (impersonateMutation.isPending ? "Starting..." : "Start Impersonating")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
